import React, { useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { trackEvent } from '../utils/analytics';

// モード: 'top' | 'login' | 'register' | 'free-register' | 'forgot-password'
// initialMode: 最初に表示するモード（省略時は'top'）
export default function AuthScreen({ onGuest, onLoggedIn, initialMode = 'top' }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false); // 登録完了 or リセットメール送信済み
  const [showPassword, setShowPassword] = useState(false);
  const [showCompare, setShowCompare]   = useState(false);

  const reset = () => {
    setEmail('');
    setPassword('');
    setError('');
    setDone(false);
  };

  const handleLogin = async () => {
    setError('');
    if (!email || !password) { setError('メールアドレスとパスワードを入力してください'); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) {
      setError('メールアドレスまたはパスワードがちがいます');
    } else {
      trackEvent('ondoku_login', { method: 'email' });
      onLoggedIn();
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    // 復帰後（App.js の getSession）でログイン/登録イベントを送るための目印
    localStorage.setItem('ondoku_pending_oauth', '1');
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/app` },
    });
    // 成功時はGoogleへリダイレクトされるのでここには戻ってこない。
    // エラー時（未設定など）のみここで表示する。
    if (err) {
      localStorage.removeItem('ondoku_pending_oauth');
      setLoading(false);
      setError('Googleログインを開始できませんでした：' + err.message);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    if (!email) { setError('メールアドレスを入力してください'); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/app?reset=1`,
    });
    setLoading(false);
    if (err) {
      setError('メールの送信に失敗しました：' + err.message);
    } else {
      setDone(true);
    }
  };

  const handleRegister = async () => {
    const isFree = mode === 'free-register';
    setError('');
    if (!email || !password) { setError('メールアドレスとパスワードを入力してください'); return; }
    if (password.length < 6)  { setError('パスワードは6文字以上にしてください'); return; }
    setLoading(true);
    const { data, error: err } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (err) {
      if (err.message?.includes('already registered')) {
        setError('このメールアドレスはすでに登録されています');
      } else {
        setError('登録に失敗しました：' + err.message);
      }
      return;
    }
    // families テーブルにレコード作成
    if (data?.user) {
      await supabase.from('families').insert({
        id: data.user.id,
        pin: '',
        is_premium: false,
      });
    }
    trackEvent('ondoku_signup', { method: 'email', plan: isFree ? 'free' : 'premium' });
    // 無料登録の場合はStripe決済をスキップするフラグを立てる
    if (isFree) {
      localStorage.setItem('freeRegister', '1');
    }
    // メール確認が無効の場合はそのままログイン状態になるので即onLoggedIn
    // メール確認が有効の場合は done=true でメール確認を促す画面を表示
    if (data?.session) {
      onLoggedIn();
    } else {
      setDone(true);
    }
  };

  // ── トップ画面 ──────────────────────────────
  if (mode === 'top') {
    return (
      <div className="auth-screen auth-screen-top">

        {/* 背景の装飾 */}
        <div className="auth-bg-orb auth-bg-orb1" />
        <div className="auth-bg-orb auth-bg-orb2" />
        <div className="auth-bg-orb auth-bg-orb3" />

        {/* ヒーローエリア */}
        <div className="auth-hero">
          <div className="auth-logo-sub">READING APP</div>
          <div className="auth-logo-main">Ondoku <span>Quest</span></div>

          {/* イラストエリア */}
          <div className="auth-illust">
            {/* キラキラ */}
            <svg className="auth-illust-star s1" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="#FDE68A"/>
            </svg>
            <svg className="auth-illust-star s2" width="10" height="10" viewBox="0 0 16 16" fill="none">
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="white"/>
            </svg>
            <svg className="auth-illust-star s3" width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="#FDE68A"/>
            </svg>
            <svg className="auth-illust-star s4" width="8" height="8" viewBox="0 0 16 16" fill="none">
              <path d="M8 0L9.5 6.5L16 8L9.5 9.5L8 16L6.5 9.5L0 8L6.5 6.5Z" fill="white"/>
            </svg>

            {/* 本のイラスト */}
            <svg className="auth-illust-book" width="110" height="90" viewBox="0 0 110 90" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* 左ページ */}
              <path d="M55 18 C55 18 30 14 8 22 L8 76 C30 68 55 72 55 72 Z" fill="rgba(255,255,255,0.15)" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
              {/* 右ページ */}
              <path d="M55 18 C55 18 80 14 102 22 L102 76 C80 68 55 72 55 72 Z" fill="rgba(255,255,255,0.2)" stroke="white" strokeWidth="2.5" strokeLinejoin="round"/>
              {/* 背表紙 */}
              <path d="M55 18 L55 72" stroke="white" strokeWidth="2" strokeDasharray="4 3"/>
              {/* 左ページの線 */}
              <line x1="20" y1="36" x2="48" y2="33" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="20" y1="44" x2="48" y2="41" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="20" y1="52" x2="48" y2="49" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="20" y1="60" x2="40" y2="58" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              {/* 右ページの線 */}
              <line x1="62" y1="33" x2="90" y2="36" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="62" y1="41" x2="90" y2="44" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="62" y1="49" x2="90" y2="52" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="62" y1="58" x2="80" y2="60" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeLinecap="round"/>
              {/* 影 */}
              <ellipse cx="55" cy="80" rx="38" ry="5" fill="rgba(0,0,0,0.15)"/>
            </svg>
          </div>

          <div className="auth-hero-catch">きょうも　おんどくを　<span>たのしもう！</span></div>
        </div>

        {/* ボタンエリア */}
        <div className="auth-actions">

          {/* 無料登録：メインCTA */}
          <button className="auth-btn-free" onClick={() => { reset(); setMode('free-register'); }}>
            <span className="auth-btn-free-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </span>
            <span className="auth-btn-free-body">
              <span className="auth-btn-free-title">無料でアカウントを作る</span>
              <span className="auth-btn-free-sub">クラウド保存・複数端末対応・完全無料</span>
            </span>
            <span className="auth-btn-free-arrow">›</span>
          </button>

          {/* Googleでログイン・登録（自動判定）：サブCTA */}
          <button className="auth-btn-guest" onClick={handleGoogleSignIn} disabled={loading}>
            <span className="auth-btn-guest-icon">
              <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
              </svg>
            </span>
            <span className="auth-btn-guest-body">
              <span className="auth-btn-guest-title">Googleで続ける</span>
              <span className="auth-btn-guest-sub">はじめての方は登録・2回目以降はログイン</span>
            </span>
            <span className="auth-btn-guest-arrow">›</span>
          </button>

          {/* ゲスト：サブCTA */}
          <button className="auth-btn-guest" onClick={onGuest}>
            <span className="auth-btn-guest-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
              </svg>
            </span>
            <span className="auth-btn-guest-body">
              <span className="auth-btn-guest-title">ゲストではじめる</span>
              <span className="auth-btn-guest-sub">ログイン不要・端末内のみ</span>
            </span>
            <span className="auth-btn-guest-arrow">›</span>
          </button>

          {error && <div className="auth-error">{error}</div>}

          {/* ログイン・プレミアム：テキストリンク */}
          <div className="auth-sub-btns">
            <button className="auth-sub-btn" onClick={() => { reset(); setMode('login'); }}>
              ログイン
            </button>
            <div className="auth-sub-sep" />
            <button className="auth-sub-btn auth-sub-btn-register" onClick={() => { reset(); setMode('register'); }}>
              👑 プレミアム登録
            </button>
          </div>

        </div>

        {/* 3tier比較トグル */}
        <div className="auth-compare-wrap">
          <button className="auth-compare-toggle" onClick={() => setShowCompare(true)}>
            ゲスト・無料・プレミアムの違いは？
          </button>
        </div>

        {/* LPリンク */}
        <div className="auth-lp-link-wrap">
          <a className="auth-lp-link" href="/lp" onClick={e => { e.preventDefault(); window.location.href = '/lp'; }}>
            📖 アプリの説明をみる
          </a>
        </div>

        {/* 3tier比較モーダル */}
        {showCompare && (
          <div className="auth-compare-modal-overlay" onClick={() => setShowCompare(false)}>
            <div className="auth-compare-modal auth-compare-modal-3col" onClick={e => e.stopPropagation()}>
              <div className="auth-compare-table auth-compare-table-3col">
                <div className="auth-compare-header">
                  <div style={{ fontSize: 11, opacity: 0.8 }}>ちがい</div>
                  <div>ゲスト</div>
                  <div style={{ color: '#60a5fa' }}>無料ログイン</div>
                  <div style={{ color: '#fbbf24' }}>👑 プレミアム</div>
                </div>
                {[
                  ['料金',             '無料',       '無料',           null],
                  ['ほんばん採点',     'おためし5回', '2回/日',         '10回/日'],
                  ['きろく保存',       '端末内のみ', 'クラウド同期',   'クラウド同期'],
                  ['AI追加購入',       '×',          '○（都度課金）',  '○（都度課金）'],
                  ['複数端末',         '×',          '○',              '○'],
                  ['音声クラウド保存', '×',          '○（3件）',       '○（50件）'],
                  ['プロフィール',     '1人まで',    '2人まで',        '5人まで'],
                ].map(([label, guest, free, premium]) => (
                  <div key={label} className="auth-compare-row auth-compare-row-3col">
                    <div className="auth-compare-label">{label}</div>
                    <div className="auth-compare-guest">{guest}</div>
                    <div className="auth-compare-free">{free}</div>
                    <div className="auth-compare-premium">
                      {label === '料金' ? '680円（買い切り）' : premium}
                    </div>
                  </div>
                ))}
              </div>
              <button className="auth-compare-close" onClick={() => setShowCompare(false)}>
                とじる
              </button>
            </div>
          </div>
        )}

        <div className="auth-footer">
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="auth-footer-link">プライバシーポリシー</a>
          <span className="auth-footer-sep">・</span>
          <a href="/terms.html"   target="_blank" rel="noopener noreferrer" className="auth-footer-link">利用規約</a>
          <span className="auth-footer-sep">・</span>
          <a href="/tokushoho.html" target="_blank" rel="noopener noreferrer" className="auth-footer-link">特定商取引法に基づく表記</a>
        </div>
      </div>
    );
  }

  // ── パスワードリセットメール送信完了 ───────────
  if (done && mode === 'forgot-password') {
    return (
      <div className="auth-screen">
        <div className="auth-form-wrap">
          <div className="auth-done-icon">📧</div>
          <div className="auth-done-title">リセットメールを送りました</div>
          <div className="auth-done-sub">
            <strong>{email}</strong> にパスワード再設定のメールを送りました。<br />
            メール内のリンクをクリックして、新しいパスワードを設定してください。
          </div>
          <button className="auth-btn-primary" onClick={() => { setDone(false); setMode('login'); }}>
            ログイン画面へ
          </button>
        </div>
      </div>
    );
  }

  // ── 登録完了画面 ────────────────────────────
  if (done) {
    return (
      <div className="auth-screen">
        <div className="auth-form-wrap">
          <div className="auth-done-icon">✅</div>
          <div className="auth-done-title">登録メールを送りました</div>
          <div className="auth-done-sub">
            <strong>{email}</strong> に確認メールを送りました。<br />
            メールのリンクをクリックしてから、ログインしてください。
          </div>
          <button className="auth-btn-primary" onClick={() => { setDone(false); setMode('login'); }}>
            ログイン画面へ
          </button>
        </div>
      </div>
    );
  }

  // ── パスワードを忘れた ───────────────────────
  if (mode === 'forgot-password') {
    return (
      <div className="auth-screen">
        <button className="auth-back-btn" onClick={() => { reset(); setMode('login'); }}>← もどる</button>
        <div className="auth-form-wrap">
          <div className="auth-form-icon">🔓</div>
          <div className="auth-form-title">パスワードの再設定</div>
          <div className="auth-forgot-desc">
            登録したメールアドレスを入力してください。<br />
            パスワード再設定のメールをお送りします。
          </div>
          <div className="auth-field">
            <label className="auth-label">メールアドレス</label>
            <input
              className="auth-input"
              type="email"
              placeholder="example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          {error && <div className="auth-error">{error}</div>}
          <button
            className="auth-btn-primary"
            onClick={handleForgotPassword}
            disabled={loading}
          >
            {loading ? '…' : 'リセットメールを送る'}
          </button>
        </div>
      </div>
    );
  }

  // ── ログイン / 新規登録 / 無料登録フォーム ───
  const isRegister = mode === 'register';
  const isFreeRegister = mode === 'free-register';
  const formIcon  = isFreeRegister ? '🎉' : isRegister ? '✨' : '🔑';
  const formTitle = isFreeRegister ? '無料アカウント登録' : isRegister ? 'プレミアム登録' : 'ログイン';
  return (
    <div className="auth-screen">
      <button className="auth-back-btn" onClick={() => { reset(); setMode('top'); }}>← もどる</button>

      <div className="auth-form-wrap">
        <div className="auth-form-icon">{formIcon}</div>
        <div className="auth-form-title">{formTitle}</div>

        {/* 無料登録のメリット説明 */}
        {isFreeRegister && (
          <div className="auth-register-notice auth-register-notice-free">
            <div className="auth-register-notice-title">✅ 完全無料・クレカ不要</div>
            <div className="auth-free-benefits">
              <div className="auth-free-benefit-item">☁️ きろくをクラウドに保存</div>
              <div className="auth-free-benefit-item">📱 複数の端末で使える</div>
              <div className="auth-free-benefit-item">👥 きょうだい2人まで登録</div>
              <div className="auth-free-benefit-item">💳 AI採点の追加購入ができる</div>
            </div>
          </div>
        )}

        {/* プレミアム登録の説明 */}
        {isRegister && (
          <div className="auth-register-notice">
            <div className="auth-register-notice-title">💳 プレミアム会員の登録です</div>
            <div className="auth-register-notice-body">
              登録後、決済画面に進みます。<br />
              お支払い完了でプレミアム機能が使えます。
            </div>
            <div className="auth-register-notice-price">
              <span className="auth-notice-price-normal">680円（買い切り）</span>
            </div>
          </div>
        )}

        <div className="auth-field">
          <label className="auth-label">メールアドレス</label>
          <input
            className="auth-input"
            type="email"
            placeholder="example@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete={isRegister ? 'email' : 'username'}
          />
        </div>

        <div className="auth-field">
          <label className="auth-label">パスワード{isRegister ? '（6文字以上）' : ''}</label>
          <div className="auth-input-wrap">
            <input
              className="auth-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
            {password && <button
              type="button"
              className="auth-eye-btn"
              onClick={() => setShowPassword(v => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
            >
              {showPassword ? (
                // 目を開く（表示中）
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="4"/>
                </svg>
              ) : (
                // 目を閉じる（非表示中）
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              )}
            </button>}
          </div>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <button
          className="auth-btn-primary"
          onClick={(isRegister || isFreeRegister) ? handleRegister : handleLogin}
          disabled={loading}
        >
          {loading ? '…' : (isRegister || isFreeRegister) ? '登録する' : 'ログイン'}
        </button>

        {/* パスワードを忘れた（ログイン画面のみ） */}
        {!isRegister && !isFreeRegister && (
          <button
            className="auth-forgot-btn"
            onClick={() => { reset(); setMode('forgot-password'); }}
          >
            パスワードを忘れた方はこちら
          </button>
        )}

        {/* Google連携の案内（ログイン画面のみ） */}
        {!isRegister && !isFreeRegister && (
          <div className="auth-google-hint">
            ログイン後、設定画面からGoogleログインに切りかえられます
          </div>
        )}

        {/* スイッチリンク */}
        {!isFreeRegister && (
          <button
            className="auth-btn-switch"
            onClick={() => { reset(); setMode(isRegister ? 'login' : 'free-register'); }}
          >
            {isRegister ? 'すでにアカウントをお持ちの方' : 'アカウントをお持ちでない方（無料登録）'}
          </button>
        )}
        {isFreeRegister && (
          <button
            className="auth-btn-switch"
            onClick={() => { reset(); setMode('login'); }}
          >
            すでにアカウントをお持ちの方
          </button>
        )}
      </div>
    </div>
  );
}
